import { logger } from '@teko/log';
import {
  FCM_REGISTER_TOKEN_SUCCESS,
  FCM_TOKEN,
  FCM_TRANSPORT,
  FCM_USER_ID,
} from '@teko/notification';
import type { NotificationService } from '@teko/notification';
import type { Profile } from '@teko/user';
import type { MessagePayload } from 'firebase/messaging';
import { ObjectMap } from 'types';

const getFcmToken = async (
  vapidKey: string,
  firebaseConfig: ObjectMap,
  onNoti: (payload?: MessagePayload) => Promise<void>,
): Promise<string> => {
  const { getMessaging, getToken, onMessage } = await import(
    'firebase/messaging'
  );
  const { getApp, initializeApp } = await import('firebase/app');
  // step 1 delete pushdy service worker
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const pushdyUrl = location.protocol + '//' + location.host + '/sw.js';
    registrations.forEach((reg) => {
      if (reg?.active?.scriptURL === pushdyUrl) {
        reg.unregister();
      }
    });
  } catch (e) {
    logger.error(e);
  }
  let firebaseNotiApp;
  const firebaseName = 'notification';
  try {
    // try to get app by name
    // it will throw exception otherwise
    firebaseNotiApp = getApp(firebaseName);
  } catch (_) {
    // initialize if not exist yet
    firebaseNotiApp = initializeApp(firebaseConfig, firebaseName);
  }

  // step 2 register firebase cloud messaging
  const messaging = getMessaging(firebaseNotiApp);

  const token = await getToken(messaging, {
    vapidKey,
  });

  onMessage(messaging, onNoti);

  return token;
};

export const registerFcmToken = async (
  firebaseConfig: ObjectMap,
  vapidKey: string,
  user: Profile,
  notificationAppId: string,
  notificationService: NotificationService,
  onNoti?: (payload: MessagePayload) => Promise<void>,
): Promise<string> => {
  const fcmToken = await getFcmToken(vapidKey, firebaseConfig, onNoti);
  const lsToken = localStorage.getItem(FCM_TOKEN);
  const lsUserId = localStorage.getItem(FCM_USER_ID);
  const lsRegisterTokenSuccess = localStorage.getItem(
    FCM_REGISTER_TOKEN_SUCCESS,
  );
  const registerCondition =
    // when firebase updates token, when first time users using the app
    fcmToken !== lsToken ||
    // When the user logs in
    (user?.userId && user.userId !== lsUserId) ||
    // When registration failed last time
    lsRegisterTokenSuccess === 'false';

  if (registerCondition) {
    try {
      await notificationService.registerToken({
        app_id: notificationAppId,
        user_id: user?.userId ?? undefined,
        transport: FCM_TRANSPORT,
        token: fcmToken,
        email: user?.email ?? undefined,
        phone: user?.telephone ?? undefined,
      });
      localStorage.setItem(FCM_REGISTER_TOKEN_SUCCESS, 'true');
      if (user?.userId) {
        localStorage.setItem(FCM_USER_ID, user.userId);
      }
    } catch (e) {
      localStorage.setItem(FCM_REGISTER_TOKEN_SUCCESS, 'false');
    }
  }
  localStorage.setItem(FCM_TOKEN, fcmToken);
  return fcmToken;
};

export const unregisterFcmToken = async (
  notificationService: NotificationService,
  notificationAppId: string,
): Promise<void> => {
  try {
    const lsToken = localStorage.getItem(FCM_TOKEN);
    const lsUserId = localStorage.getItem(FCM_USER_ID);
    await notificationService.unRegisterToken({
      user_id: lsUserId,
      app_id: notificationAppId,
      transport: FCM_TRANSPORT,
      token: lsToken,
    });
  } finally {
    localStorage.removeItem(FCM_USER_ID);
  }
};
